import React, { useState, useEffect, useRef, useCallback } from 'react';

const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

interface CalledToken {
  token_number: number;
  customer_name: string;
  service?: string;
  called_at?: string;
}

interface CounterDisplay {
  code: string;
  name: string;
  description?: string;
  current_token: number;
  called_token: CalledToken | null;
}

interface DisplayData {
  queue_name: string;
  is_open: boolean;
  counters: CounterDisplay[];
  waiting_count: number;
  done_count: number;
  today_total: number;
}

// ── Speak announcement ────────────────────────────────────────────────────────

const ANNOUNCEMENTS: Record<string, (digits: string, counter: string) => string> = {
  en: (d, c) => `Token number ${d}. Please proceed to ${c}.`,
  hi: (d, c) => `टोकन नंबर ${d}. कृपया ${c} पर जाएं।`,
  ar: (d, c) => `رقم التوكن ${d.replace(/[0-9]/g, n => '٠١٢٣٤٥٦٧٨٩'[+n])}. يرجى التوجه إلى ${c}.`,
  ta: (d, c) => `டோக்கன் எண் ${d}. தயவுசெய்து ${c} க்கு செல்லவும்.`,
  te: (d, c) => `టోకెన్ నంబర్ ${d}. దయచేసి ${c} కి వెళ్ళండి.`,
  kn: (d, c) => `ಟೋಕನ್ ನಂಬರ್ ${d}. ದಯವಿಟ್ಟು ${c} ಗೆ ಹೋಗಿ.`,
  ml: (d, c) => `ടോക്കൺ നമ്പർ ${d}. ദയവായി ${c} ലേക്ക് പോകൂ.`,
  mr: (d, c) => `टोकन क्रमांक ${d}. कृपया ${c} कडे जा.`,
  gu: (d, c) => `ટોકन નંબর ${d}. કૃपा करके ${c} पर जाएं।`,
  pa: (d, c) => `ਟੋਕਨ ਨੰਬਰ ${d}. ਕਿਰਪਾ ਕਰਕੇ ${c} ਤੇ ਜਾਓ।`,
  bn: (d, c) => `টোকেন নম্বর ${d}. অনুগ্রহ করে ${c} এ যান।`,
  ur: (d, c) => `ٹوکن نمبر ${d}. براہ کرم ${c} پر جائیں۔`,
  fr: (d, c) => `Numéro de ticket ${d}. Veuillez vous rendre au ${c}.`,
  es: (d, c) => `Número de turno ${d}. Por favor diríjase a ${c}.`,
  de: (d, c) => `Tokennummer ${d}. Bitte gehen Sie zu ${c}.`,
  'zh-CN': (d, c) => `号码 ${d}。请前往 ${c}。`,
  ja: (d, c) => `番号 ${d}。${c} へお越しください。`,
  ko: (d, c) => `번호 ${d}. ${c} 로 오십시오.`,
  ru: (d, c) => `Номер талона ${d}. Пожалуйста пройдите к ${c}.`,
  tr: (d, c) => `Token numarası ${d}. Lütfen ${c} e gidin.`,
  vi: (d, c) => `Số thứ tự ${d}. Vui lòng đến ${c}.`,
  th: (d, c) => `หมายเลข ${d}. กรุณาไปที่ ${c}.`,
  id: (d, c) => `Nomor token ${d}. Silakan menuju ke ${c}.`,
  ms: (d, c) => `Nombor token ${d}. Sila pergi ke ${c}.`,
  it: (d, c) => `Numero token ${d}. Si prega di recarsi a ${c}.`,
  nl: (d, c) => `Tokennummer ${d}. Gaat u alstublieft naar ${c}.`,
  pl: (d, c) => `Numer tokenu ${d}. Proszę udać się do ${c}.`,
  he: (d, c) => `מספר תור ${d}. אנא פנה אל ${c}.`,
  fa: (d, c) => `شماره نوبت ${d}. لطفاً به ${c} بروید.`,
  uk: (d, c) => `Номер талона ${d}. Будь ласка пройдіть до ${c}.`,
  ro: (d, c) => `Numărul tokenului ${d}. Vă rugăm să mergeți la ${c}.`,
  el: (d, c) => `Αριθμός token ${d}. Παρακαλώ προχωρήστε στο ${c}.`,
  cs: (d, c) => `Číslo tokenu ${d}. Prosím jděte k ${c}.`,
  hu: (d, c) => `Token szám ${d}. Kérjük menjen a ${c}.`,
  fi: (d, c) => `Token numero ${d}. Ole hyvä ja mene ${c}.`,
  sv: (d, c) => `Tokennummer ${d}. Vänligen gå till ${c}.`,
  no: (d, c) => `Tokennummer ${d}. Vennligst gå til ${c}.`,
  da: (d, c) => `Tokennummer ${d}. Gå venligst til ${c}.`,
  si: (d, c) => `ටෝකන් අංකය ${d}. කරුණාකර ${c} වෙත යන්න.`,
  ne: (d, c) => `टोकन नम्बर ${d}. कृपया ${c} मा जानुहोस्।`,
  my: (d, c) => `တိုကင်နံပါတ် ${d}။ ကျေးဇူးပြု၍ ${c} သို့သွားပါ။`,
  km: (d, c) => `លេខសំបុត្រ ${d}។ សូមទៅកាន់ ${c}។`,
  lo: (d, c) => `ເລກໂທເຄັນ ${d}. ກະລຸນາໄປທີ່ ${c}.`,
  am: (d, c) => `የቶከን ቁጥር ${d}። እባክዎ ወደ ${c} ይሂዱ።`,
  so: (d, c) => `Lambarka token ${d}. Fadlan aad ${c}.`,
  ha: (d, c) => `Lambar token ${d}. Don Allah je ${c}.`,
  yo: (d, c) => `Nọmba token ${d}. Jọwọ lọ si ${c}.`,
  zu: (d, c) => `Inombolo ye-token ${d}. Sicela uye ku ${c}.`,
  sw: (d, c) => `Nambari ya token ${d}. Tafadhali nenda kwa ${c}.`,
// European extras
bg: (d, c) => `Номер на жетона ${d}. Моля отидете до ${c}.`,
hr: (d, c) => `Broj tokena ${d}. Molimo idite do ${c}.`,
sk: (d, c) => `Číslo tokenu ${d}. Prosím choďte k ${c}.`,
sl: (d, c) => `Številka žetona ${d}. Prosimo pojdite k ${c}.`,
sr: (d, c) => `Broj tokena ${d}. Molimo idite do ${c}.`,
lt: (d, c) => `Žetono numeris ${d}. Prašome eiti prie ${c}.`,
lv: (d, c) => `Žetona numurs ${d}. Lūdzu dodieties uz ${c}.`,
et: (d, c) => `Žetoni number ${d}. Palun minge ${c} juurde.`,
is: (d, c) => `Númer tákns ${d}. Vinsamlegast farðu til ${c}.`,
mk: (d, c) => `Број на токен ${d}. Ве молиме одете кај ${c}.`,
sq: (d, c) => `Numri i tokenit ${d}. Ju lutemi shkoni tek ${c}.`,
bs: (d, c) => `Broj tokena ${d}. Molimo idite do ${c}.`,
ca: (d, c) => `Número de torn ${d}. Si us plau aneu a ${c}.`,
gl: (d, c) => `Número de quenda ${d}. Por favor vai a ${c}.`,
cy: (d, c) => `Rhif tocyn ${d}. Ewch i ${c} os gwelwch yn dda.`,
ga: (d, c) => `Uimhir comhartha ${d}. Téigh go ${c} le do thoil.`,
mt: (d, c) => `Numru tal-token ${d}. Jekk jogħġbok mur ${c}.`,
pt: (d, c) => `Número do token ${d}. Por favor dirija-se a ${c}.`,
af: (d, c) => `Token nommer ${d}. Gaan asseblief na ${c}.`,
ps: (d, c) => `د ټوکن شمیره ${d}. مهرباني وکړئ ${c} ته لاړ شئ.`,
// Asian extras
mn: (d, c) => `Токений дугаар ${d}. ${c} рүү явна уу.`,
jv: (d, c) => `Nomer token ${d}. Mangga menyang ${c}.`,
su: (d, c) => `Nomer token ${d}. Punten angkat ka ${c}.`,
fil: (d, c) => `Token numero ${d}. Mangyaring pumunta sa ${c}.`,
ka: (d, c) => `ტოკენის ნომერი ${d}. გთხოვთ გახვიდეთ ${c}-ში.`,
kk: (d, c) => `Токен нөмірі ${d}. ${c} жаққа өтіңіз.`,
az: (d, c) => `Token nömrəsi ${d}. Zəhmət olmasa ${c} a keçin.`,
uz: (d, c) => `Token raqami ${d}. Iltimos ${c} ga o'ting.`,
};

// ── Browser Web Speech Fallback (Male & Female) ──
function speakWithBrowser(text: string, lang = 'en', gender: 'female' | 'male' = 'female'): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      resolve();
      return;
    }
    try {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = lang;
      utterance.rate = 0.9;

      const voices = window.speechSynthesis.getVoices();
      const matching = voices.filter(v => v.lang.toLowerCase().startsWith(lang.split('-')[0].toLowerCase()));

      const maleKeywords = ['david', 'mark', 'george', 'ravi', 'guy', 'alex', 'fred', 'male', 'madhur'];
      const femaleKeywords = ['zira', 'samantha', 'victoria', 'heera', 'aria', 'female', 'swara', 'pallavi', 'sapna'];
      const targetKeywords = gender === 'male' ? maleKeywords : femaleKeywords;

      const pickedVoice = matching.find(v => targetKeywords.some(k => v.name.toLowerCase().includes(k))) || matching[0] || voices[0];
      if (pickedVoice) utterance.voice = pickedVoice;

      let done = false;
      utterance.onend = () => { if (!done) { done = true; resolve(); } };
      utterance.onerror = () => { if (!done) { done = true; resolve(); } };
      setTimeout(() => { if (!done) { done = true; resolve(); } }, 6000);

      window.speechSynthesis.speak(utterance);
    } catch {
      resolve();
    }
  });
}

async function announce(tokenNum: number, counterName: string, ctx: AudioContext | null): Promise<void> {
  const digits = String(tokenNum).padStart(3, '0').split('').join(' ');
  const urlLangs = new URLSearchParams(window.location.search).get('langs');
  const langs = urlLangs
    ? urlLangs.split(',').filter(Boolean)
    : [1, 2, 3].map(n => localStorage.getItem(`tts_lang_${n}`) || '').filter(Boolean);
  if (langs.length === 0) langs.push('en');

  return new Promise((resolve) => {
    const playNext = async (index: number) => {
      console.log(`[display] playNext called, index=${index}, langs=`, langs);
      if (index >= langs.length) { resolve(); return; }
      const lang = langs[index];
      const fn = ANNOUNCEMENTS[lang] || ANNOUNCEMENTS['en'];
      const text = fn(digits, counterName);
      const gender = (new URLSearchParams(window.location.search).get('gender') || 'female') as 'female' | 'male';
      const fallback = async () => {
        try {
          await speakWithBrowser(text, lang, gender);
        } catch {}
        playNext(index + 1);
      };

      try {
        const response = await fetch(`${BASE}/queue/tts?text=${encodeURIComponent(text)}&lang=${lang}&gender=${gender}`);
        if (!response.ok) throw new Error(`Server TTS error: ${response.status}`);
        const arrayBuffer = await response.arrayBuffer();

        if (!ctx) {
          await fallback();
          return;
        }

        const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(ctx.destination);
        source.onended = () => { playNext(index + 1); };
        source.start(0);
      } catch (err) {
        console.warn(`[display] [${lang}] Server TTS failed, falling back to browser speech:`, err);
        await fallback();
      }
    };

    console.log('[display] announce() called for token', tokenNum, 'langs from URL/localStorage:', langs);
    playNext(0);
  });
}
function playChime(ctx: AudioContext | null): Promise<void> {
  return new Promise((resolve) => {
    if (!ctx) { resolve(); return; }
    try {
      // Pleasant chime: C5 E5 G5
      [523, 659, 784].forEach((freq, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = 'sine';
        o.frequency.value = freq;
        const t = ctx.currentTime + i * 0.18;
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.4, t + 0.04);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.6);
        o.start(t); o.stop(t + 0.6);
      });
      setTimeout(resolve, 700);
    } catch {
      resolve();
    }
  });
}
// ── Announcement queue (serializes overlapping calls) ─────────────────────────

interface QueuedAnnouncement { tokenNum: number; counterName: string }

function createAnnouncementQueue(getCtx: () => AudioContext | null, getVoiceEnabled: () => boolean) {
  const queue: QueuedAnnouncement[] = [];
  let processing = false;

  const processNext = async () => {
    if (processing) return;
    const next = queue.shift();
    if (!next) return;
    processing = true;

    const ctx = getCtx();
    await playChime(ctx);
    if (getVoiceEnabled()) {
      await announce(next.tokenNum, next.counterName, ctx);
    }

    processing = false;
    processNext();
  };

  return {
    push: (tokenNum: number, counterName: string) => {
      queue.push({ tokenNum, counterName });
      processNext();
    },
  };
}
// ── Recent calls ticker ───────────────────────────────────────────────────────

interface RecentCall { token: number; counter: string; time: string }

// ── Main ──────────────────────────────────────────────────────────────────────

export default function QueueDisplayPage() {
  const params = new URLSearchParams(window.location.search);
  const displayToken = params.get('token') || '';
  const outletId = params.get('outlet_id') || '';
  const counterCode = params.get('counter') || '';

  const [data, setData] = useState<DisplayData | null>(null);
  const [error, setError] = useState('');
  const [recentCalls, setRecentCalls] = useState<RecentCall[]>([]);
  const [flashCounters, setFlashCounters] = useState<Set<string>>(new Set());
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
const audioCtxRef = useRef<AudioContext | null>(null);
const announceQueueRef = useRef<ReturnType<typeof createAnnouncementQueue> | null>(null);
  if (!announceQueueRef.current) {
    announceQueueRef.current = createAnnouncementQueue(
      () => audioCtxRef.current,
      () => voiceEnabled
    );
  }
  const resolvedOrgIdRef = useRef<string>('');
  const [time, setTime] = useState(new Date());
  const [pageIndex, setPageIndex] = useState(0);
  const lastAnnounceRef = useRef<string>('');
// Seed on first load so we don't replay old announcements
  const prevTokensRef = useRef<Record<string, number>>({});
  const prevRingRef = useRef<Record<string, string>>({});
  const intervalRef = useRef<any>(null);
  const tickRef = useRef<any>(null);
  const fetchDisplay = useCallback(async () => {
  if (!displayToken) { setError('Add ?token=YOUR_DISPLAY_TOKEN to the URL'); return; }
  try {
    const q = (outletId ? `&outlet_id=${outletId}` : '') + (counterCode ? `&counter=${counterCode}` : '');
    const r = await fetch(`${BASE}/queue/display?token=${displayToken}${q}`);
      if (!r.ok) { setError(`Display not found (${r.status})`); return; }
      const d: DisplayData & { org_id?: string } = await r.json();
      if (d.org_id) resolvedOrgIdRef.current = d.org_id;

      setData(prev => {
        if (prev) {
          // Detect newly called tokens
          d.counters.forEach(c => {
            const prevToken = prevTokensRef.current[c.code] ?? 0;
            const prevRing = prevRingRef.current[c.code] ?? '';
            const newRing = (c as any).last_ring_at ?? '';
            const isNewToken = c.current_token > 0 && c.current_token !== prevToken;
            const isRingAgain = newRing && newRing !== prevRing && !isNewToken;
          if (isNewToken || isRingAgain) {
  console.log('[display] NEW TOKEN DETECTED at', new Date().toISOString(), c.code, 'token:', c.current_token, 'isNewToken:', isNewToken, 'isRingAgain:', isRingAgain);
  // New call or ring again — push into the queue so overlapping calls play one at a time
  announceQueueRef.current!.push(c.current_token, c.name);

  setFlashCounters(prev => new Set(prev).add(c.code));
  setTimeout(() => {
    setFlashCounters(prev => {
      const next = new Set(prev);
      next.delete(c.code);
      return next;
    });
  }, 3000);

  setRecentCalls(rc => [
    { token: c.current_token, counter: c.name, time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) },
    ...rc.slice(0, 9),
  ]);
}
            prevTokensRef.current[c.code] = c.current_token;
            prevRingRef.current[c.code] = newRing;
          });
        } else {
          // First load — seed prevTokens without announcing
          d.counters.forEach(c => {
            prevTokensRef.current[c.code] = c.current_token;
            prevRingRef.current[c.code] = (c as any).last_ring_at ?? '';
          });
        }
        return d;
      });
      setError('');
    } catch {
      setError('Cannot reach server');
    }
  }, [displayToken, outletId, counterCode, voiceEnabled]);

  useEffect(() => {
    let announceInterval: any;

    fetchDisplay().then(() => {
      // Poll for custom announcements — uses the resolved org_id (never shown in the URL)
      const announceQ = outletId ? `&outlet_id=${outletId}` : '';
      const orgId = resolvedOrgIdRef.current;
      if (!orgId) return;

      fetch(`${BASE}/queue/announce?org_id=${orgId}${announceQ}`)
        .then(r => r.json())
        .then(d => { if (d?.at) lastAnnounceRef.current = d.at; })
        .catch(() => {});
    });
    intervalRef.current = setInterval(fetchDisplay, 4000);
    tickRef.current = setInterval(() => setTime(new Date()), 1000);

    announceInterval = setInterval(async () => {
      const orgId = resolvedOrgIdRef.current;
      if (!orgId || !voiceEnabled) return;
      const announceQ = outletId ? `&outlet_id=${outletId}` : '';
      try {
        const r = await fetch(`${BASE}/queue/announce?org_id=${orgId}${announceQ}`);
        const d = await r.json();
        if (d?.at && d.at !== lastAnnounceRef.current) {
          lastAnnounceRef.current = d.at;
          const gender = (d.gender || 'female') as 'female' | 'male';
          try {
            const r2 = await fetch(`${BASE}/queue/tts?text=${encodeURIComponent(d.text)}&lang=${d.lang}&gender=${gender}`);
            if (!r2.ok) throw new Error('Server TTS failed');
            const blob = await r2.blob();
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            audio.onended = () => URL.revokeObjectURL(url);
            audio.onerror = async () => {
              URL.revokeObjectURL(url);
              await speakWithBrowser(d.text, d.lang, gender);
            };
            await audio.play().catch(async () => {
              URL.revokeObjectURL(url);
              await speakWithBrowser(d.text, d.lang, gender);
            });
          } catch {
            await speakWithBrowser(d.text, d.lang, gender);
          }
        }
      } catch {}
    }, 3000);

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(announceInterval);
      clearInterval(tickRef.current);
    };
  }, [fetchDisplay]);

  // ── Multi-screen layout: split counters into pages and auto-rotate ──
  const layoutParam = params.get('layout') || 'all'; // 'all' | '1' | '2' | '3'
  const rotateSecs = Number(params.get('rotate')) || 8;
  const allCounters = data?.counters || [];
  const perScreen = layoutParam === 'all' ? allCounters.length : Math.max(1, parseInt(layoutParam) || allCounters.length);
  const pages: CounterDisplay[][] = [];
  for (let i = 0; i < allCounters.length; i += perScreen) {
    pages.push(allCounters.slice(i, i + perScreen));
  }
  if (pages.length === 0) pages.push([]);

  useEffect(() => {
    if (pages.length <= 1) return;
    const timer = setInterval(() => setPageIndex(p => (p + 1) % pages.length), rotateSecs * 1000);
    return () => clearInterval(timer);
  }, [pages.length, rotateSecs]);

  const safePageIndex = pageIndex % pages.length;
  const visibleCounters = pages[safePageIndex];

  if (!displayToken) return <Fullscreen><OrgHint /></Fullscreen>;
  if (error && !data) return <Fullscreen><ErrorMsg msg={error} /></Fullscreen>;
  if (!data) return <Fullscreen><Spinner /></Fullscreen>;


  const hasCounters = data.counters.length > 0;
  const cols = visibleCounters.length <= 2 ? visibleCounters.length : visibleCounters.length <= 4 ? 2 : 3;

  return (
    
    <div style={{
      minHeight: '100vh', background: '#ffffff',
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      display: 'flex', flexDirection: 'column',
      userSelect: 'none',
    }}>
{!audioUnlocked && (
  <div onClick={() => {
    // Unlock audio context with user gesture — store it so later sounds can reuse it
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      ctx.resume();
      audioCtxRef.current = ctx;
    } catch {}
    setAudioUnlocked(true);
  }} style={{
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(15,23,42,0.85)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexDirection: 'column', gap: 16, cursor: 'pointer',
  }}>
    <div style={{ fontSize: 64 }}>🔊</div>
    <div style={{ fontSize: 24, fontWeight: 900, color: '#fff' }}>Tap to Enable Sound</div>
    <div style={{ fontSize: 14, color: '#94a3b8' }}>Click anywhere to activate voice announcements</div>
    <div style={{ marginTop: 8, padding: '12px 32px', borderRadius: 12, background: '#6366f1', color: '#fff', fontWeight: 800, fontSize: 16 }}>
      Start Display
    </div>
  </div>
)}
      {/* ── Top bar ── */}
      <div style={{
        background: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        padding: '0 40px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        height: 72,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', letterSpacing: -0.5 }}>{data.queue_name}</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#6366f1', letterSpacing: 3, textTransform: 'uppercase', marginTop: 3 }}>Queue Display System</div>
          </div>
          <div style={{
            padding: '4px 14px', borderRadius: 99, fontSize: 12, fontWeight: 800,
            background: data.is_open ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
            color: data.is_open ? '#22c55e' : '#ef4444',
            border: `1px solid ${data.is_open ? '#22c55e44' : '#ef444444'}`,
          }}>
            {data.is_open ? '● OPEN' : '● CLOSED'}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          {/* Stats */}
          {[
            { label: 'Waiting', value: data.waiting_count, color: '#6366f1' },
            { label: 'Served', value: data.done_count, color: '#22c55e' },
            { label: 'Today', value: data.today_total, color: '#94a3b8' },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 10, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{s.label}</div>
            </div>
          ))}

          {/* Clock */}
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: '#0f172a', fontVariantNumeric: 'tabular-nums', letterSpacing: 1 }}>
              {time.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
            </div>
            <div style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>
              {time.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
          </div>

          {/* Voice toggle */}
          <button onClick={() => setVoiceEnabled(v => !v)}
            style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${voiceEnabled ? '#22c55e44' : '#33415588'}`, background: 'transparent', color: voiceEnabled ? '#22c55e' : '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            {voiceEnabled ? '🔊' : '🔇'}
          </button>
        </div>
      </div>

      {/* ── Main content ── */}
      <div style={{ flex: 1, display: 'flex', gap: 0 }}>

        {/* Counter panels */}
        <div style={{ flex: 1, padding: '32px 32px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>

          {!hasCounters ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 64, opacity: 0.2 }}>📍</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#334155' }}>No counters configured</div>
              <div style={{ fontSize: 14, color: '#1e293b' }}>Add counters from the Queue Manager admin panel</div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gap: 20,
              flex: 1,
            }}>
              {visibleCounters.map(counter => {
                const isFlashing = flashCounters.has(counter.code);
                const hasToken = counter.current_token > 0;
                return (
                  <div key={counter.code} style={{
                    background: isFlashing
                      ? 'linear-gradient(135deg, #f0fdf4, #dcfce7)'
                      : '#f8fafc',
                    border: `2px solid ${isFlashing ? '#22c55e' : '#e2e8f0'}`,
                    borderRadius: 20,
                    padding: '32px 28px',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    gap: 12,
                    boxShadow: isFlashing ? '0 0 48px rgba(34,197,94,0.25)' : '0 4px 24px rgba(0,0,0,0.3)',
                    transition: 'all 0.4s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: 240,
                  }}>
                    {/* Glow overlay when flashing */}
                    {isFlashing && (
                      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at center, rgba(34,197,94,0.12) 0%, transparent 70%)', animation: 'glow-pulse 1s ease-in-out infinite' }} />
                    )}

                    {/* Counter name */}
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', textAlign: 'center' }}>
                      {counter.name}
                    </div>
                    {counter.description && (
                      <div style={{ fontSize: 12, color: '#475569', textAlign: 'center' }}>{counter.description}</div>
                    )}

                    {/* Token number — BIG */}
                    <div style={{
                      fontSize: visibleCounters.length <= 2 ? 120 : visibleCounters.length <= 4 ? 96 : 72,
                      fontWeight: 900,
                      color: isFlashing ? '#16a34a' : hasToken ? '#0f172a' : '#cbd5e1',
                      letterSpacing: 6,
                      lineHeight: 1,
                      fontVariantNumeric: 'tabular-nums',
                      textShadow: isFlashing ? '0 0 40px rgba(34,197,94,0.6)' : hasToken ? '0 0 30px rgba(255,255,255,0.1)' : 'none',
                      transition: 'all 0.3s ease',
                      zIndex: 1,
                    }}>
                      {hasToken ? String(counter.current_token).padStart(3, '0') : '---'}
                    </div>

                    {/* Patient name */}
                    {counter.called_token && (
                      <div style={{ textAlign: 'center', zIndex: 1 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: isFlashing ? '#16a34a' : '#475569' }}>
                          {counter.called_token.customer_name}
                        </div>
                        {counter.called_token.service && (
                          <div style={{ fontSize: 13, color: '#475569', marginTop: 3 }}>{counter.called_token.service}</div>
                        )}
                      </div>
                    )}

                    {/* "Please proceed" label */}
                    {isFlashing && (
                      <div style={{ fontSize: 14, fontWeight: 800, color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.12em', animation: 'blink 0.8s step-end infinite', zIndex: 1 }}>
                        Please Proceed ▶
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {pages.length > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
              {pages.map((_, i) => (
                <div key={i} style={{
                  width: i === safePageIndex ? 24 : 8, height: 8, borderRadius: 4,
                  background: i === safePageIndex ? '#6366f1' : '#e2e8f0',
                  transition: 'all 0.3s ease',
                }} />
              ))}
            </div>
          )}

          {/* ── Bottom message ── */}
          <div style={{ textAlign: 'center', padding: '12px 0', borderTop: '1px solid #0f172a' }}>
            <div style={{ fontSize: 14, color: '#334155', fontWeight: 600 }}>
              Keep your token slip safe · Scan the QR code on your slip to track your position on your phone
            </div>
          </div>
        </div>

        {/* ── Recent calls sidebar ── */}
        {recentCalls.length > 0 && (
          <div style={{
            width: 240, background: '#f8fafc', borderLeft: '1px solid #e2e8f0',
            padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 12,
            overflowY: 'auto',
          }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
              Recent Calls
            </div>
            {recentCalls.map((rc, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 12px', borderRadius: 10,
                background: i === 0 ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${i === 0 ? '#22c55e22' : '#1e293b'}`,
              }}>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: i === 0 ? '#22c55e' : '#475569', letterSpacing: 2 }}>
                    {String(rc.token).padStart(3, '0')}
                  </div>
                  <div style={{ fontSize: 10, color: '#334155', marginTop: 2 }}>{rc.counter}</div>
                </div>
                <div style={{ fontSize: 10, color: '#334155', fontVariantNumeric: 'tabular-nums' }}>{rc.time}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ textAlign:'center', padding:'10px 0', borderTop:'1px solid #1e293b', fontSize:9, fontWeight:700, letterSpacing:2, color:'#334155' }}>
        POWERED BY CLOUDAIRAMBO™
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes glow-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
      `}</style>
    </div>
  );
}

// ── Helper components ─────────────────────────────────────────────────────────

function Fullscreen({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#060c18',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    }}>
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <div style={{ width: 48, height: 48, border: '4px solid #1e293b', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <div style={{ color: '#334155', fontSize: 16, fontWeight: 600 }}>Loading display...</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ErrorMsg({ msg }: { msg: string }) {
  return (
    <div style={{ textAlign: 'center', color: '#ef4444' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
      <div style={{ fontSize: 18, fontWeight: 700 }}>{msg}</div>
    </div>
  );
}



function OrgHint() {
  return (
    <div style={{ textAlign: 'center', color: '#334155', maxWidth: 480, padding: 32 }}>
      <div style={{ fontSize: 64, marginBottom: 20 }}>📺</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#475569', marginBottom: 12 }}>Queue Display Screen</div>
      <div style={{ fontSize: 15, lineHeight: 1.7 }}>
        Add your display link's token to the URL:<br />
        <code style={{ background: '#0f172a', color: '#6366f1', padding: '4px 12px', borderRadius: 8, fontSize: 14, display: 'inline-block', marginTop: 8 }}>
          /queue/display?token=YOUR_DISPLAY_TOKEN&counter=COUNTER_CODE
        </code>
      </div>
      <div style={{ marginTop: 20, fontSize: 13, color: '#1e293b' }}>
        Copy the link for each counter from the Queue Manager admin panel — Settings.
      </div>
    </div>
  );
}
